import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Card } from '../ui/Card'
import { Button } from '../ui/Button'
import { Badge } from '../ui/Badge'
import { 
  X, 
  MapPin, 
  Phone, 
  Mail, 
  Globe,
  Star,
  Users,
  Truck,
  DollarSign,
  Award,
  CheckCircle,
  ExternalLink,
  MessageCircle,
  Heart,
  Info
} from 'lucide-react'
import { Vendor } from '../../services/mealmateService'

interface VendorDetailsModalProps {
  isOpen: boolean
  onClose: () => void
  vendor: Vendor
  onSelectMeals: (vendorId: string) => void
}

const VendorDetailsModal: React.FC<VendorDetailsModalProps> = ({
  isOpen,
  onClose,
  vendor,
  onSelectMeals
}) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'menu' | 'reviews' | 'contact'>('overview')
  const [isFavorited, setIsFavorited] = useState(false)

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${
          i < Math.floor(rating) 
            ? 'text-yellow-400 fill-current' 
            : i < rating 
            ? 'text-yellow-400 fill-current opacity-50'
            : 'text-theme-text-muted'
        }`}
      />
    ))
  }

  const getOperatingStatus = () => {
    const now = new Date()
    const currentDay = now.toLocaleDateString('en-US', { weekday: 'long' })
    const currentTime = now.toLocaleTimeString('en-US', { 
      hour12: false, 
      hour: '2-digit', 
      minute: '2-digit' 
    })

    const todaySchedule = vendor.operatingHours.find(day => day.day === currentDay)
    
    if (!todaySchedule || !todaySchedule.isOpen) {
      return { status: 'Closed', color: 'text-theme-error', bgColor: 'bg-theme-error/10' }
    }

    const [openHour, openMinute] = todaySchedule.open.split(':').map(Number)
    const [closeHour, closeMinute] = todaySchedule.close.split(':').map(Number)
    const [currentHour, currentMinute] = currentTime.split(':').map(Number)

    const openTime = openHour * 60 + openMinute
    const closeTime = closeHour * 60 + closeMinute
    const currentTimeMinutes = currentHour * 60 + currentMinute

    if (currentTimeMinutes >= openTime && currentTimeMinutes <= closeTime) {
      return { status: 'Open', color: 'text-theme-success', bgColor: 'bg-theme-success/10' }
    } else {
      return { status: 'Closed', color: 'text-theme-error', bgColor: 'bg-theme-error/10' }
    }
  }

  const operatingStatus = getOperatingStatus()

  const sampleReviews = [
    {
      id: '1',
      name: 'Sarah Johnson',
      rating: 5,
      date: '2 days ago',
      comment: 'Amazing food quality and fast delivery! The ingredients were super fresh and the packaging was eco-friendly.',
      verified: true
    },
    {
      id: '2',
      name: 'Mike Chen',
      rating: 4,
      date: '1 week ago',
      comment: 'Great taste and portion sizes. Delivery was on time and the driver was very polite.',
      verified: true
    },
    {
      id: '3',
      name: 'Emily Rodriguez',
      rating: 5,
      date: '2 weeks ago',
      comment: 'Love their healthy options! Perfect for my dietary needs and always consistent quality.',
      verified: true
    }
  ]

  const sampleMenu = [
    {
      id: '1',
      name: 'Grilled Salmon Bowl',
      description: 'Fresh Atlantic salmon with quinoa and roasted vegetables',
      price: 18.99,
      category: 'Healthy',
      image: 'https://trae-api-us.mchost.guru/api/ide/v1/text_to_image?prompt=Professional%20food%20photography%20of%20grilled%20salmon%20bowl%20with%20quinoa%20and%20roasted%20vegetables%2C%20fresh%20healthy%20meal%2C%20restaurant%20quality%20presentation%2C%20natural%20lighting&image_size=square',
      nutrition: { calories: 450, protein: 35, carbs: 25, fat: 20 },
      dietaryTags: ['High-Protein', 'Healthy'],
      allergens: ['Fish'],
      prepTime: 25,
      isAvailable: true,
      ingredients: ['Salmon', 'Quinoa', 'Asparagus', 'Olive Oil', 'Lemon'],
      detailedIngredients: [
        { name: 'Atlantic Salmon', origin: 'animal', category: 'animal', description: 'Sustainably farmed', transparencyInfo: 'Sourced from Norway' },
        { name: 'Quinoa', origin: 'natural', category: 'plant', description: 'Organic white quinoa' },
        { name: 'Olive Oil', origin: 'natural', category: 'plant', description: 'Extra virgin cold pressed' },
        { name: 'Lemon Zest', origin: 'natural', category: 'plant' },
        { name: 'Sea Salt', origin: 'natural', category: 'mineral' }
      ],
      dietaryClaims: {
        isVegan: false,
        isVegetarian: false,
        isGlutenFree: true,
        isDairyFree: true,
        isNatural: true,
        isSyntheticFree: true
      },
      synergyAnalysis: {
        score: 92,
        recommendation: 'highly_recommended',
        summary: 'Excellent bioavailability profile. The healthy fats from salmon promote absorption of fat-soluble vitamins.',
        interactions: [
          {
            ingredients: ['Salmon (Omega-3)', 'Asparagus (Vitamin K)'],
            type: 'positive',
            effect: 'bioavailability',
            description: 'Fatty acids significantly increase Vitamin K absorption.',
            scientificScore: 9
          },
          {
            ingredients: ['Lemon (Vitamin C)', 'Quinoa (Iron)'],
            type: 'positive',
            effect: 'bioavailability',
            description: 'Vitamin C boosts plant-based iron absorption by up to 300%.',
            scientificScore: 10
          }
        ]
      }
    },
    {
      id: '2',
      name: 'Mediterranean Quinoa Salad',
      description: 'Fresh vegetables, feta cheese, and lemon herb dressing',
      price: 14.99,
      category: 'Salad',
      image: 'https://trae-api-us.mchost.guru/api/ide/v1/text_to_image?prompt=Professional%20food%20photography%20of%20Mediterranean%20quinoa%20salad%20with%20fresh%20vegetables%20and%20feta%20cheese%2C%20healthy%20salad%2C%20restaurant%20quality%20presentation&image_size=square',
      nutrition: { calories: 320, protein: 12, carbs: 45, fat: 15 },
      dietaryTags: ['Vegetarian', 'Healthy'],
      allergens: ['Dairy'],
      prepTime: 15,
      isAvailable: true,
      ingredients: ['Quinoa', 'Feta Cheese', 'Cucumber', 'Tomatoes', 'Olives'],
      detailedIngredients: [
        { name: 'Quinoa', origin: 'natural', category: 'plant' },
        { name: 'Feta Cheese', origin: 'animal', category: 'animal', description: 'Sheep milk feta' },
        { name: 'Organic Cucumber', origin: 'natural', category: 'plant' },
        { name: 'Cherry Tomatoes', origin: 'natural', category: 'plant' },
        { name: 'Kalamata Olives', origin: 'natural', category: 'plant' },
        { name: 'Vitamin D3 Fortification', origin: 'synthetic', category: 'chemical', isSynthetic: true, transparencyInfo: 'Added for nutritional balance' }
      ],
      dietaryClaims: {
        isVegan: false,
        isVegetarian: true,
        isGlutenFree: true,
        isDairyFree: false,
        isNatural: false,
        isSyntheticFree: false
      },
      synergyAnalysis: {
        score: 45,
        recommendation: 'caution',
        summary: 'Contains some conflicting combinations. Dairy calcium may inhibit iron absorption.',
        interactions: [
          {
            ingredients: ['Feta Cheese (Calcium)', 'Quinoa (Iron)'],
            type: 'negative',
            effect: 'bioavailability',
            description: 'Calcium competes with iron for absorption pathways.',
            scientificScore: 7
          },
          {
            ingredients: ['Tomatoes', 'Olive Oil'],
            type: 'positive',
            effect: 'bioavailability',
            description: 'Heat and fat increase lycopene absorption.',
            scientificScore: 8
          }
        ]
      }
    }
  ]

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50"
              onClick={onClose}
            />

            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="relative bg-theme-surface rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden border border-theme-border"
            >
              {/* Header */}
              <div className="relative h-64 bg-gradient-to-br from-green-100 to-emerald-100">
                <img
                  src={vendor.logo || `https://trae-api-us.mchost.guru/api/ide/v1/text_to_image?prompt=Professional%20restaurant%20banner%20image%2C%20${vendor.name.replace(' ', '%20')}%2C%20food%20establishment%20branding%2C%20high-quality%20restaurant%20photography%2C%20appetizing%20food%20display&image_size=landscape_16_9`}
                  alt={vendor.name}
                  className="w-full h-full object-cover rounded-t-2xl"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent rounded-t-2xl" />
                
                {/* Header Content */}
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <div className="flex items-end justify-between">
                    <div>
                      <h1 className="text-3xl font-bold mb-2">{vendor.name}</h1>
                      <p className="text-lg opacity-90">{vendor.description}</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setIsFavorited(!isFavorited)}
                        className="p-2 bg-white/20 backdrop-blur-sm rounded-full hover:bg-white/30 transition-colors"
                      >
                        <Heart className={`w-5 h-5 ${isFavorited ? 'text-red-500 fill-current' : 'text-white'}`} />
                      </button>
                      <button
                        onClick={onClose}
                        className="p-2 bg-white/20 backdrop-blur-sm rounded-full hover:bg-white/30 transition-colors"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Navigation Tabs */}
              <div className="flex border-b border-theme-border bg-theme-surface-light">
                {[
                  { id: 'overview', name: 'Overview', icon: Info },
                  { id: 'menu', name: 'Menu', icon: Users },
                  { id: 'reviews', name: 'Reviews', icon: Star },
                  { id: 'contact', name: 'Contact', icon: Phone }
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as any)}
                    className={`flex-1 px-6 py-4 text-sm font-medium transition-colors flex items-center justify-center space-x-2 ${
                      activeTab === tab.id
                        ? 'text-theme-primary border-b-2 border-theme-primary bg-theme-surface'
                        : 'text-theme-text-secondary hover:text-theme-text-primary'
                    }`}
                  >
                    <tab.icon className="w-4 h-4" />
                    <span>{tab.name}</span>
                  </button>
                ))}
              </div>

              {/* Content */}
              <div className="p-6 overflow-y-auto max-h-[50vh]">
                {activeTab === 'overview' && (
                  <div className="space-y-6">
                    {/* Status and Rating */}
                    <div className="flex items-center justify-between p-4 bg-theme-surface-light rounded-lg">
                      <div className="flex items-center space-x-4">
                        <div className={`px-3 py-1 rounded-full ${operatingStatus.bgColor}`}>
                          <span className={`text-sm font-medium ${operatingStatus.color}`}>
                            {operatingStatus.status}
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          {renderStars(vendor.rating)}
                          <span className="text-sm text-theme-text-secondary">({vendor.rating})</span>
                        </div>
                      </div>
                      <div className="text-sm text-theme-text-secondary">
                        {vendor.deliveryTime} delivery
                      </div>
                    </div>

                    {/* Key Info */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="p-4 bg-theme-success/5 rounded-lg border border-theme-success/20">
                        <div className="flex items-center space-x-2 mb-2">
                          <Truck className="w-5 h-5 text-theme-success" />
                          <h3 className="font-semibold text-theme-text-primary">Delivery</h3>
                        </div>
                        <p className="text-sm text-theme-text-secondary">
                          {vendor.deliveryFee === 0 ? 'Free delivery' : `$${vendor.deliveryFee} delivery fee`}
                        </p>
                        <p className="text-xs text-theme-text-muted mt-1">{vendor.deliveryTime}</p>
                      </div>

                      <div className="p-4 bg-theme-primary/5 rounded-lg border border-theme-primary/20">
                        <div className="flex items-center space-x-2 mb-2">
                          <DollarSign className="w-5 h-5 text-theme-primary" />
                          <h3 className="font-semibold text-theme-text-primary">Minimum Order</h3>
                        </div>
                        <p className="text-sm text-theme-text-secondary">${vendor.minimumOrder} minimum</p>
                        <p className="text-xs text-theme-text-muted mt-1">Order threshold</p>
                      </div>

                      <div className="p-4 bg-theme-accent/5 rounded-lg border border-theme-accent/20">
                        <div className="flex items-center space-x-2 mb-2">
                          <Award className="w-5 h-5 text-theme-accent" />
                          <h3 className="font-semibold text-theme-text-primary">Rating</h3>
                        </div>
                        <p className="text-sm text-theme-text-secondary">{vendor.rating}/5 stars</p>
                        <p className="text-xs text-theme-text-muted mt-1">Customer satisfaction</p>
                      </div>
                    </div>

                    {/* Cuisine and Dietary */}
                    <div className="space-y-4">
                      <div>
                        <h3 className="font-semibold text-theme-text-primary mb-3">Cuisine Types</h3>
                        <div className="flex flex-wrap gap-2">
                          {vendor.cuisineTypes.map((cuisine) => (
                            <Badge key={cuisine} className="bg-theme-success/10 text-theme-success">
                              {cuisine}
                            </Badge>
                          ))}
                        </div>
                      </div>

                      <div>
                        <h3 className="font-semibold text-theme-text-primary mb-3">Dietary Options</h3>
                        <div className="flex flex-wrap gap-2">
                          {vendor.dietaryOptions.map((dietary) => (
                            <Badge key={dietary} className="bg-theme-primary/10 text-theme-primary">
                              {dietary}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Operating Hours */}
                    <div>
                      <h3 className="font-semibold text-theme-text-primary mb-3">Operating Hours</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {vendor.operatingHours.map((day) => (
                          <div key={day.day} className="flex items-center justify-between p-2 bg-theme-surface-light rounded">
                            <span className="text-sm font-medium text-theme-text-secondary">{day.day}</span>
                            <div className="text-sm text-theme-text-secondary">
                              {day.isOpen ? (
                                <span className="text-theme-success">{day.open} - {day.close}</span>
                              ) : (
                                <span className="text-theme-error">Closed</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'menu' && (
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <h3 className="text-xl font-semibold text-theme-text-primary">Popular Menu Items</h3>
                      <Button
                        onClick={() => onSelectMeals(vendor.id)}
                        className="bg-theme-success hover:bg-theme-success/90 text-theme-text-inverse"
                      >
                        View Full Menu
                      </Button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {sampleMenu.map((meal) => (
                        <Card key={meal.id} className="hover:shadow-md transition-shadow">
                          <div className="p-4">
                            <div className="flex items-start space-x-4">
                              <img
                                src={meal.image}
                                alt={meal.name}
                                className="w-16 h-16 rounded-lg object-cover"
                              />
                              <div className="flex-1">
                                <h4 className="font-semibold text-theme-text-primary mb-1">{meal.name}</h4>
                                <p className="text-sm text-theme-text-secondary mb-2">{meal.description}</p>
                                <div className="flex items-center justify-between">
                                  <span className="text-lg font-bold text-theme-success">${meal.price}</span>
                                  <Badge className="bg-theme-success/10 text-theme-success">
                                    {meal.prepTime}min
                                  </Badge>
                                </div>
                              </div>
                            </div>
                          </div>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}

                {activeTab === 'reviews' && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-xl font-semibold text-theme-text-primary">Customer Reviews</h3>
                      <div className="flex items-center space-x-2">
                        {renderStars(vendor.rating)}
                        <span className="text-sm text-theme-text-secondary">({vendor.rating})</span>
                      </div>
                    </div>

                    {sampleReviews.map((review) => (
                      <Card key={review.id} className="p-4">
                        <div className="flex items-start space-x-4">
                          <div className="w-10 h-10 bg-gradient-to-r from-green-600 to-emerald-600 rounded-full flex items-center justify-center">
                            <span className="text-white font-semibold text-sm">
                              {review.name.charAt(0)}
                            </span>
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-2">
                              <div>
                                <h4 className="font-semibold text-theme-text-primary">{review.name}</h4>
                                <div className="flex items-center space-x-2">
                                  {renderStars(review.rating)}
                                  {review.verified && (
                                    <Badge className="bg-theme-success/10 text-theme-success text-xs">
                                      <CheckCircle className="w-3 h-3 mr-1" />
                                      Verified
                                    </Badge>
                                  )}
                                </div>
                              </div>
                              <span className="text-sm text-theme-text-muted">{review.date}</span>
                            </div>
                            <p className="text-theme-text-secondary">{review.comment}</p>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}

                {activeTab === 'contact' && (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <Card className="p-6">
                        <h3 className="font-semibold text-theme-text-primary mb-4">Contact Information</h3>
                        <div className="space-y-3">
                           <div className="flex items-center space-x-3">
                            <Phone className="w-5 h-5 text-theme-success" />
                            <span className="text-theme-text-secondary">{vendor.contactInfo.phone}</span>
                          </div>
                          <div className="flex items-center space-x-3">
                            <Mail className="w-5 h-5 text-theme-success" />
                            <span className="text-theme-text-secondary">{vendor.contactInfo.email}</span>
                          </div>
                          {vendor.contactInfo.website && (
                            <div className="flex items-center space-x-3">
                              <Globe className="w-5 h-5 text-theme-success" />
                              <a 
                                href={vendor.contactInfo.website}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-theme-primary hover:text-theme-primary/80"
                              >
                                Visit Website
                              </a>
                            </div>
                          )}
                        </div>
                      </Card>

                      <Card className="p-6">
                        <h3 className="font-semibold text-theme-text-primary mb-4">Location</h3>
                        <div className="space-y-3">
                          <div className="flex items-center space-x-3">
                            <MapPin className="w-5 h-5 text-theme-success" />
                            <span className="text-theme-text-secondary">{vendor.contactInfo.address}</span>
                          </div>
                        </div>
                      </Card>
                    </div>

                    <div className="flex space-x-4">
                      <Button className="flex-1 bg-theme-success hover:bg-theme-success/90 text-theme-text-inverse">
                        <Phone className="w-4 h-4 mr-2" />
                        Call Now
                      </Button>
                      <Button variant="outline" className="flex-1">
                        <MessageCircle className="w-4 h-4 mr-2" />
                        Send Message
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between p-6 border-t border-theme-border bg-theme-surface-light">
                <div className="text-sm text-theme-text-secondary">
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-theme-success" />
                    <span>Verified Partner</span>
                  </div>
                </div>
                
                <div className="flex space-x-3">
                  <Button
                    variant="outline"
                    onClick={onClose}
                    className="border-theme-border text-theme-text-secondary hover:bg-theme-surface"
                  >
                    Close
                  </Button>
                  <Button
                    onClick={() => {
                      onSelectMeals(vendor.id)
                      onClose()
                    }}
                    className="bg-theme-success hover:bg-theme-success/90 text-theme-text-inverse"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    View Full Menu
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      )}
    </AnimatePresence>
  )
}

export default VendorDetailsModal